// app/javascript/controllers/purchases_grouping_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["table", "toggleButton", "toggleText"]
  static values = { 
    supplierColumnIndex: Number,
    amountColumnIndex: Number,
    paidAmountColumnIndex: Number
  }

  connect() {
    this.isGrouped = false
    this.originalTableBody = null
    this.setupGrouping()
    this.restoreUserPreference()
  }

  setupGrouping() {
    // Store original table body for restoration
    const tbody = this.tableTarget.querySelector('tbody')
    this.originalTableBody = tbody.cloneNode(true)
    
    // Add CSS classes to document head
    this.injectStyles()
    
    // Auto-detect column indices if not provided
    this.detectColumnIndices()
  }

  detectColumnIndices() {
    const headerRow = this.tableTarget.querySelector('thead tr')
    if (!headerRow) return

    const headers = Array.from(headerRow.querySelectorAll('th'))
    
    // Auto-detect columns by header text if values not provided
    if (!this.hasSupplierColumnIndexValue) {
      this.supplierColumnIndexValue = headers.findIndex(th => 
        th.textContent.toLowerCase().includes('supplier')
      )
    }
    
    if (!this.hasAmountColumnIndexValue) {
      this.amountColumnIndexValue = headers.findIndex(th => 
        th.textContent.toLowerCase().includes('amount') && 
        !th.textContent.toLowerCase().includes('paid')
      )
    }
    
    if (!this.hasPaidAmountColumnIndexValue) {
      this.paidAmountColumnIndexValue = headers.findIndex(th => 
        th.textContent.toLowerCase().includes('paid')
      )
    }
  }

  toggle() {
    this.isGrouped = !this.isGrouped
    
    if (this.isGrouped) {
      this.applyGrouping()
      this.updateToggleButton("Hide Grouping", "btn-info")
    } else {
      this.removeGrouping()
      this.updateToggleButton("Group by Supplier", "btn-outline-info")
    }
    
    // Store preference
    localStorage.setItem('purchasesGrouped', this.isGrouped)
  }

  applyGrouping() {
    const tbody = this.tableTarget.querySelector('tbody')
    const rows = Array.from(tbody.querySelectorAll('tr:not(.supplier-group-header)'))
    
    if (rows.length === 0) return
    
    // Group consecutive rows by supplier
    const groups = this.groupConsecutiveRows(rows)
    
    // Clear tbody
    tbody.innerHTML = ''
    
    // Add grouped rows
    groups.forEach((group, groupIndex) => {
      // Add group header if more than one purchase
      if (group.purchases.length > 1) {
        tbody.appendChild(this.createGroupHeader(group, groupIndex))
      }
      
      // Add purchases with appropriate classes
      group.purchases.forEach((row, purchaseIndex) => {
        this.applyGroupClasses(row, groupIndex, purchaseIndex, group.purchases.length)
        this.modifySupplierCell(row, purchaseIndex, group.purchases.length)
        tbody.appendChild(row)
      })
    })
    
    // Apply visual styling
    document.body.classList.add('purchases-grouped')
  }

  removeGrouping() {
    const tbody = this.tableTarget.querySelector('tbody')
    
    // Restore original table body
    tbody.innerHTML = this.originalTableBody.innerHTML
    
    // Remove visual styling
    document.body.classList.remove('purchases-grouped')
  }

  groupConsecutiveRows(rows) {
    if (rows.length === 0) return []
    
    const groups = []
    let currentGroup = {
      supplier: this.getSupplierFromRow(rows[0]),
      purchases: [rows[0]]
    }
    
    for (let i = 1; i < rows.length; i++) {
      const rowSupplier = this.getSupplierFromRow(rows[i])
      
      if (rowSupplier === currentGroup.supplier) {
        currentGroup.purchases.push(rows[i])
      } else {
        groups.push(currentGroup)
        currentGroup = {
          supplier: rowSupplier,
          purchases: [rows[i]]
        }
      }
    }
    
    groups.push(currentGroup)
    return groups
  }

  getSupplierFromRow(row) {
    // First try to find cell with data-supplier attribute
    const supplierCell = row.querySelector('[data-supplier]')
    if (supplierCell) {
      return supplierCell.querySelector('a')?.textContent.trim() || 
             supplierCell.textContent.trim()
    }
    
    // Fallback to column index
    if (this.supplierColumnIndexValue >= 0 && row.cells[this.supplierColumnIndexValue]) {
      const cell = row.cells[this.supplierColumnIndexValue]
      return cell.querySelector('a')?.textContent.trim() || 
             cell.textContent.trim()
    }
    
    return ""
  }

  createGroupHeader(group, groupIndex) {
    const headerRow = document.createElement('tr')
    headerRow.className = 'supplier-group-header'
    headerRow.dataset.group = groupIndex
    
    const columnCount = this.tableTarget.querySelector('thead tr')?.children.length || 9
    const totalAmount = this.calculateGroupTotal(group.purchases, this.amountColumnIndexValue)
    const paidAmount = this.calculateGroupTotal(group.purchases, this.paidAmountColumnIndexValue)
    
    headerRow.innerHTML = `
      <td colspan="${columnCount}" class="bg-light border-left border-primary py-2" style="border-left-width: 4px !important;">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <strong class="text-primary">
              <i class="fas fa-building"></i>
              ${this.escapeHtml(group.supplier)}
            </strong>
            <small class="text-muted ml-2">
              (${group.purchases.length} consecutive purchase${group.purchases.length > 1 ? 's' : ''})
            </small>
          </div>
          <div class="text-right">
            <small class="text-muted">
              Total: ${totalAmount} •
              Paid: ${paidAmount}
            </small>
          </div>
        </div>
      </td>
    `
    
    return headerRow
  }

  calculateGroupTotal(purchases, columnIndex) {
    if (columnIndex < 0) return "N/A"
    
    const total = purchases.reduce((sum, row) => {
      if (!row.cells[columnIndex]) return sum
      
      const cellText = row.cells[columnIndex].textContent.trim()
      const amount = this.extractNumericValue(cellText)
      return sum + amount
    }, 0)
    
    return this.formatCurrency(total)
  }

  extractNumericValue(text) {
    // Remove currency symbols, commas, and extract numeric value
    const cleaned = text.replace(/[^\d.-]/g, '')
    return parseFloat(cleaned) || 0
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat('en-KE', { 
      style: 'currency', 
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount)
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  applyGroupClasses(row, groupIndex, purchaseIndex, totalInGroup) {
    // Remove any existing group classes
    row.className = row.className.replace(/group-\w+/g, '').trim()
    
    // Add group identification
    row.dataset.group = groupIndex
    row.dataset.supplierGroup = 'true'
    
    // Add position classes
    if (totalInGroup === 1) {
      row.classList.add('group-single')
    } else if (purchaseIndex === 0) {
      row.classList.add('group-first')
    } else if (purchaseIndex === totalInGroup - 1) {
      row.classList.add('group-last')
    } else {
      row.classList.add('group-middle')
    }
  }

  modifySupplierCell(row, purchaseIndex, totalInGroup) {
    const supplierCell = this.getSupplierCellFromRow(row)
    if (!supplierCell) return
    
    const supplierLink = supplierCell.querySelector('a')
    
    if (totalInGroup > 1 && purchaseIndex > 0) {
      // Hide supplier name for non-first items in group
      if (supplierLink) {
        supplierLink.style.display = 'none'
      }
      
      // Check if indicator already exists
      if (!supplierCell.querySelector('.supplier-continuation')) {
        // Add indicator
        const indicator = document.createElement('span')
        indicator.className = 'text-muted supplier-continuation'
        indicator.innerHTML = '<i class="fas fa-arrow-up"></i> Same as above'
        supplierCell.appendChild(indicator)
      }
    }
  }

  getSupplierCellFromRow(row) {
    // First try data attribute
    const cellWithAttribute = row.querySelector('[data-supplier]')
    if (cellWithAttribute) return cellWithAttribute
    
    // Fallback to column index
    if (this.supplierColumnIndexValue >= 0 && row.cells[this.supplierColumnIndexValue]) {
      return row.cells[this.supplierColumnIndexValue]
    }
    
    return null
  }

  updateToggleButton(text, buttonClass) {
    this.toggleTextTarget.textContent = text
    this.toggleButtonTarget.className = `btn ${buttonClass} ml-2`
  }

  restoreUserPreference() {
    const savedPreference = localStorage.getItem('purchasesGrouped')
    if (savedPreference === 'true' && !this.isGrouped) {
      // Delay to ensure DOM is ready
      setTimeout(() => this.toggle(), 100)
    }
  }

  injectStyles() {
    if (document.getElementById('purchases-grouping-styles')) return
    
    const styles = document.createElement('style')
    styles.id = 'purchases-grouping-styles'
    styles.textContent = `
      /* Purchases Grouping Styles */
      .purchases-grouped .group-first {
        border-top: 3px solid #007bff !important;
      }
      
      .purchases-grouped .group-last {
        border-bottom: 3px solid #007bff !important;
      }
      
      .purchases-grouped .group-single {
        border-top: 3px solid #6c757d !important;
        border-bottom: 3px solid #6c757d !important;
      }
      
      .purchases-grouped .supplier-group-header {
        background: linear-gradient(135deg, #f8f9fc 0%, #e3e6f0 100%);
        font-weight: 500;
      }
      
      .purchases-grouped tr[data-group="0"] { background-color: #f8f9fa; }
      .purchases-grouped tr[data-group="1"] { background-color: #ffffff; }
      .purchases-grouped tr[data-group="2"] { background-color: #f8f9fa; }
      .purchases-grouped tr[data-group="3"] { background-color: #ffffff; }
      .purchases-grouped tr[data-group="4"] { background-color: #f8f9fa; }
      .purchases-grouped tr[data-group="5"] { background-color: #ffffff; }
      
      .supplier-continuation {
        font-size: 0.8em;
        font-style: italic;
        display: block;
        margin-top: 4px;
      }
      
      .purchases-grouped .supplier-group-header:hover {
        background: linear-gradient(135deg, #e3e6f0 0%, #d1ecf1 100%);
      }
      
      .purchases-grouped .group-first,
      .purchases-grouped .group-last,
      .purchases-grouped .group-single {
        transition: border-color 0.2s ease;
      }
    `
    
    document.head.appendChild(styles)
  }

  disconnect() {
    // Clean up styles when controller is disconnected
    const styles = document.getElementById('purchases-grouping-styles')
    if (styles) styles.remove()
    
    // Remove grouping class
    document.body.classList.remove('purchases-grouped')
  }
}
